import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import useVoiceRecorder from '../hooks/useVoiceRecorder';
import { toast } from 'sonner';
import {
  X, Send, Loader2, ChevronDown, Zap, HelpCircle,
  RotateCcw, Terminal, Package, Truck, FileText,
  DollarSign, Search, Clock, Mic, MicOff, Volume2, VolumeX, ShoppingCart,
  Sparkles, TrendingUp, AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DARK_LOGO = 'https://customer-assets.emergentagent.com/job_inventory-core-11/artifacts/43j2lsb0_Tirth-Logo-Final-File-01.png';

const ACTION_BADGE = {
  ADD_STOCK:       { label: 'Stock Added',     cls: 'bg-green-900/60 text-green-300 border-green-700' },
  REDUCE_STOCK:    { label: 'Stock Reduced',   cls: 'bg-orange-900/60 text-orange-300 border-orange-700' },
  RECORD_PURCHASE: { label: 'Feed Bill',       cls: 'bg-blue-900/60 text-blue-300 border-blue-700' },
  CREATE_BATCH:    { label: 'Batch Created',   cls: 'bg-purple-900/60 text-purple-300 border-purple-700' },
  FEASIBILITY:     { label: 'Feasibility',     cls: 'bg-amber-900/60 text-amber-300 border-amber-700' },
  QUERY_STOCK:     { label: 'Stock Query',     cls: 'bg-zinc-800 text-zinc-400 border-zinc-700' },
  CLIENT_LEDGER:   { label: 'Ledger',          cls: 'bg-cyan-900/60 text-cyan-300 border-cyan-700' },
  UNDO:            { label: 'Undone',          cls: 'bg-red-900/60 text-red-300 border-red-700' },
  UNDO_HISTORY:    { label: 'Action Log',      cls: 'bg-zinc-800 text-zinc-400 border-zinc-700' },
  AI_ANALYSIS:     { label: 'AI Insight',      cls: 'bg-violet-900/60 text-violet-300 border-violet-700' },
  REMINDER_ASSISTANT: { label: 'Reminder · Gemini', cls: 'bg-sky-900/60 text-sky-300 border-sky-700' },
  DISPATCH_FG:     { label: 'Dispatched',      cls: 'bg-teal-900/60 text-teal-300 border-teal-700' },
  SET_PRICE:       { label: 'Price Updated',   cls: 'bg-emerald-900/60 text-emerald-300 border-emerald-700' },
  PENDING_ORDERS:  { label: 'Orders',          cls: 'bg-blue-900/60 text-blue-300 border-blue-700' },
  OVERDUE:         { label: 'Overdue',         cls: 'bg-red-900/60 text-red-300 border-red-700' },
  BATCH_STATUS:    { label: 'Batches',         cls: 'bg-purple-900/60 text-purple-300 border-purple-700' },
  HELP:            { label: 'Help',            cls: 'bg-zinc-800 text-zinc-400 border-zinc-700' },
  UNKNOWN:         { label: 'Unknown',         cls: 'bg-zinc-800 text-zinc-500 border-zinc-700' },
  CONFIRM_PROMPT:  { label: 'Confirm?',        cls: 'bg-yellow-900/60 text-yellow-300 border-yellow-700' },
  GENERAL:         { label: '',                cls: '' },
};

const QUICK_CMDS = [
  { text: 'show all stock', icon: Package },
  { text: 'low stock', icon: Search },
  { text: 'pending orders', icon: FileText },
  { text: 'recent batches', icon: Clock },
  { text: 'help', icon: HelpCircle },
];

const COMMAND_SUGGESTIONS = [
  'add 50 kg turmeric',
  'less 20 kg chili',
  'feedbill 100 kg chili at 85',
  'get turmeric',
  'show all stock',
  'show finished goods',
  'low stock',
  'made 200 kg garam masala',
  'can i make 500 kg garam masala',
  'dispatched 100 kg garam masala',
  'pending orders',
  'overdue',
  'recent batches',
  'ledger for ',
  'set price garam masala 250',
  'undo',
  'undo history',
  'hey ti, what is my revenue trend?',
  'help',
];

function StockTable({ headers, data }) {
  if (!headers || !data || data.length === 0) return null;
  return (
    <div className="mt-2 overflow-x-auto rounded-lg border border-zinc-700/80">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-zinc-700 bg-zinc-800/80">
            {headers.map(h => (
              <th key={h} className="text-left text-zinc-400 font-medium px-2 py-1.5 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} className="border-b border-zinc-800/40 last:border-0 hover:bg-zinc-800/40 transition-colors">
              {headers.map(h => (
                <td key={h} className={`px-2 py-1 ${
                  (h === 'Status' && (row[h] === 'LOW' || String(row[h]).includes('SHORT'))) ? 'text-red-400 font-semibold' :
                  (h === 'Status' && row[h] === 'OK') ? 'text-green-400' :
                  (h === 'Status' && row[h] === 'UNDONE') ? 'text-zinc-500 line-through' :
                  (h === 'Status' && row[h] === 'Active') ? 'text-green-400' :
                  (h.includes('Amount') || h.includes('Rate') || h.includes('Price')) ? 'text-amber-400 font-mono' :
                  (h === 'Qty' || h === 'Stock' || h.includes('Qty') || h === 'Quantity' || h === 'Deficit') ? 'text-zinc-200 font-mono' :
                  'text-zinc-300'
                }`}>
                  {row[h] ?? '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RenderMessage({ text }) {
  // Bold text: **text**, line breaks preserved
  return text.split('\n').map((line, li) => (
    <React.Fragment key={li}>
      {li > 0 && <br />}
      {line.split(/(\*\*[^*]+\*\*)/).map((part, j) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={j} className="text-amber-400">{part.slice(2, -2)}</strong>
          : part
      )}
    </React.Fragment>
  ));
}

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: "Hi! I'm **TI** — your live business brain wired into the ERP. I can look up costs, prices, debtors, revenue trends, margins, dispatches and more. Just ask.\n\nTry:\n  • *which client has highest debt?*\n  • *cost of maltodextrin*\n  • *revenue trend this quarter*\n  • *loss leader products*\n  • *feed bill 100 kg chili at 85*\n\nOr tap **Generate Insights** in the header for an auto-brief.",
      action: 'HELP',
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [sessionId] = useState(() => 'ti-' + Date.now());
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { recording, transcribing, start: startRecording, stop: stopRecording } = useVoiceRecorder({
    context: 'chat',
    onText: (text) => {
      setInput(prev => prev ? `${prev} ${text}` : text);
      setTimeout(() => inputRef.current?.focus(), 50);
    },
    onError: (msg) => toast.error(msg),
  });
  const audioRef = useRef(null);
  const [playingIdx, setPlayingIdx] = useState(null);
  // TTS auto-speak is OFF by default so the welcome doesn't blurt out on app load.
  const [ttsEnabled, setTtsEnabled] = useState(() => {
    try { return localStorage.getItem('ti_tts_enabled') === '1'; } catch { return false; }
  });
  const userHasInteractedRef = useRef(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Auto-speak only AFTER the user has interacted at least once, and only the latest assistant message.
    const last = messages[messages.length - 1];
    if (ttsEnabled && userHasInteractedRef.current && last && last.role === 'assistant' && !last._spoken) {
      last._spoken = true;
      speakMessage(messages.length - 1, last.text || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const filteredSuggestions = useMemo(() => {
    if (!input.trim()) return [];
    const q = input.toLowerCase();
    return COMMAND_SUGGESTIONS.filter(s => s.toLowerCase().includes(q) && s.toLowerCase() !== q).slice(0, 5);
  }, [input]);

  const sendMessage = async (e, overrideMsg) => {
    e?.preventDefault();
    const userMsg = (overrideMsg || input).trim();
    if (!userMsg || loading) return;
    userHasInteractedRef.current = true;
    setInput('');
    setShowSuggestions(false);
    setMessages(prev => [...prev, { role: 'user', text: userMsg, timestamp: new Date() }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/chat`, { message: userMsg, session_id: sessionId }, { withCredentials: true });
      const data = res.data;
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: data.message,
        action: data.action,
        actionResult: data.action_result,
        tableHeaders: data.table_headers,
        tableData: data.table_data,
        timestamp: new Date(),
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Error processing command. Please try again.',
        action: 'UNKNOWN',
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleQuickUndo = () => {
    sendMessage(null, 'undo');
  };

  // iter234 · spec 28 — any screen can hand a question to the real Hey TI:
  //   window.dispatchEvent(new CustomEvent('heyti:open', { detail: { message } }))
  useEffect(() => {
    const onOpen = (ev) => {
      const msg = ev?.detail?.message;
      setOpen(true);
      if (msg) setTimeout(() => sendMessage(null, msg), 250);
    };
    window.addEventListener('heyti:open', onOpen);
    return () => window.removeEventListener('heyti:open', onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter231 · Generate Insights — on-demand proactive brain
  const generateInsights = async () => {
    if (insightsLoading) return;
    userHasInteractedRef.current = true;
    setInsightsLoading(true);
    setMessages(prev => [...prev, {
      role: 'user',
      text: 'Generate business insights',
      timestamp: new Date(),
    }]);
    try {
      const res = await axios.post(`${API}/ai/insights/generate`, {}, { withCredentials: true });
      const insights = res.data.insights || [];
      if (insights.length === 0) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: "No pressing insights right now — the ERP looks clean.",
          action: 'AI_ANALYSIS',
          timestamp: new Date(),
        }]);
      } else {
        const c = res.data.counts || {};
        const summary =
          `**${insights.length} insight(s)** · ` +
          `🔴 ${c.critical || 0} critical · 🟠 ${c.high || 0} high · ` +
          `🟡 ${c.medium || 0} medium · 🟢 ${c.info || 0} info\n\n` +
          insights.map(i => {
            const icon = { critical: '🔴', high: '🟠', medium: '🟡', info: '🟢' }[i.severity] || '🟢';
            return `${icon} **${i.title}**\n   ${i.detail}\n   → *${i.action}*`;
          }).join('\n\n');
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: summary,
          action: 'AI_ANALYSIS',
          insights,
          timestamp: new Date(),
        }]);
      }
      toast.success(`${insights.length} insight(s) generated · check the bell for alerts`);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Could not generate insights right now. Please try again in a moment.',
        action: 'UNKNOWN',
        timestamp: new Date(),
      }]);
    } finally {
      setInsightsLoading(false);
    }
  };

  // ── Voice: Smart Whisper via shared hook (iOS-safe container, ERP vocabulary) ──
  const toggleRecording = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  // ── TTS: speak an assistant message ────────────────────────────────────
  const speakMessage = async (idx, text) => {
    if (!text || !text.trim()) return;
    // Stop any ongoing playback
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
    }
    if (playingIdx === idx) { setPlayingIdx(null); return; }
    setPlayingIdx(idx);
    try {
      const res = await axios.post(
        `${API}/voice/synthesize`,
        { text: text.slice(0, 1800), voice: 'nova' },
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlayingIdx(null); URL.revokeObjectURL(url); audioRef.current = null; };
      audio.onerror = () => { setPlayingIdx(null); URL.revokeObjectURL(url); audioRef.current = null; };
      await audio.play();
    } catch (err) {
      console.error('tts error', err);
      setPlayingIdx(null);
    }
  };

  const toggleTts = () => {
    const nv = !ttsEnabled;
    setTtsEnabled(nv);
    try { localStorage.setItem('ti_tts_enabled', nv ? '1' : '0'); } catch {}
    if (!nv && audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
      setPlayingIdx(null);
    }
  };

  const lastAction = messages.filter(m => m.role === 'assistant' && m.actionResult?.success).slice(-1)[0];

  return (
    <>
      {/* Floating Button */}
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen(o => !o)}
        className={`fixed bottom-16 right-5 z-50 w-12 h-12 rounded-full shadow-xl flex items-center justify-center transition-all duration-200 ${
          open
            ? 'bg-zinc-700 text-zinc-300 scale-95'
            : 'bg-red-600 hover:bg-red-500 text-white scale-100 hover:scale-110'
        }`}
        style={{ boxShadow: open ? undefined : '0 0 20px rgba(220,38,38,0.4)' }}
      >
        {open ? <X size={20} /> : <Terminal size={18} />}
      </button>

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-36 right-5 z-50 w-[370px] rounded-2xl border border-zinc-700 bg-[#0F0F11]/97 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden"
          style={{ height: '520px' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800 bg-[#0A0A0C] flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <img src={DARK_LOGO} alt="TI" className="w-7 h-7 rounded-lg object-contain" />
              <div>
                <div className="text-zinc-50 text-sm font-bold flex items-center gap-1.5">
                  TI Command Engine
                  <Terminal size={11} className="text-red-400" />
                </div>
                <div className="text-zinc-500 text-[10px] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                  Agentic AI · GPT-5.5
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={generateInsights}
                data-testid="ai-chat-generate-insights"
                disabled={insightsLoading}
                title="Generate business insights"
                className={`transition-all px-2 py-1 rounded-lg text-[10px] font-semibold flex items-center gap-1 ${
                  insightsLoading
                    ? 'bg-amber-500/20 text-amber-300 cursor-wait'
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 text-black hover:brightness-110'
                }`}
              >
                {insightsLoading
                  ? <><Loader2 size={11} className="animate-spin" /> Thinking…</>
                  : <><Sparkles size={11} /> Insights</>}
              </button>
              <button
                onClick={toggleTts}
                data-testid="ai-chat-tts-toggle"
                className={`transition-colors p-1 rounded-lg hover:bg-zinc-800 ${ttsEnabled ? 'text-emerald-400' : 'text-zinc-500'}`}
                title={ttsEnabled ? 'Voice replies ON — click to mute' : 'Voice replies OFF — click to enable'}
              >
                {ttsEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
              </button>
              {lastAction && (
                <button
                  onClick={handleQuickUndo}
                  data-testid="quick-undo-btn"
                  className="text-zinc-500 hover:text-red-400 transition-colors p-1 rounded-lg hover:bg-zinc-800"
                  title="Undo last action"
                >
                  <RotateCcw size={14} />
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-300 transition-colors p-1">
                <ChevronDown size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[92%] ${msg.role === 'user' ? '' : 'w-full'}`}>
                  <div className={`rounded-xl px-3 py-2 text-xs leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-red-600/90 text-white font-mono'
                      : 'bg-zinc-800/80 text-zinc-200 border border-zinc-700/60'
                  }`}>
                    {msg.role === 'user' ? (
                      <span className="flex items-center gap-1.5">
                        <span className="text-red-300/60">$</span>
                        {msg.text}
                      </span>
                    ) : (
                      <RenderMessage text={msg.text} />
                    )}
                  </div>

                  {/* Action badge */}
                  {msg.action && ACTION_BADGE[msg.action] && (
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${ACTION_BADGE[msg.action].cls}`}>
                        {ACTION_BADGE[msg.action].label}
                      </span>
                      {msg.actionResult?.success && (
                        <span className="text-[10px] text-green-500">Done</span>
                      )}
                      {msg.actionResult?.feasible === true && (
                        <span className="text-[10px] text-green-500">Feasible</span>
                      )}
                      {msg.actionResult?.feasible === false && (
                        <span className="text-[10px] text-red-500">Not feasible</span>
                      )}
                      {msg.role === 'assistant' && (
                        <button
                          data-testid={`ai-chat-speak-${i}`}
                          onClick={() => speakMessage(i, msg.text || '')}
                          className={`ml-auto p-1 rounded-full transition-colors ${playingIdx === i ? 'text-emerald-400 bg-emerald-900/40' : 'text-zinc-500 hover:text-emerald-400 hover:bg-zinc-800'}`}
                          title={playingIdx === i ? 'Stop' : 'Play as voice'}
                        >
                          {playingIdx === i ? <VolumeX size={12} /> : <Volume2 size={12} />}
                        </button>
                      )}
                    </div>
                  )}
                  {/* Standalone speaker when there's no action badge row */}
                  {msg.role === 'assistant' && !ACTION_BADGE[msg.action] && (
                    <div className="mt-1 flex items-center justify-end">
                      <button
                        data-testid={`ai-chat-speak-${i}`}
                        onClick={() => speakMessage(i, msg.text || '')}
                        className={`p-1 rounded-full transition-colors ${playingIdx === i ? 'text-emerald-400 bg-emerald-900/40' : 'text-zinc-500 hover:text-emerald-400 hover:bg-zinc-800'}`}
                        title={playingIdx === i ? 'Stop' : 'Play as voice'}
                      >
                        {playingIdx === i ? <VolumeX size={12} /> : <Volume2 size={12} />}
                      </button>
                    </div>
                  )}

                  {/* Stock table */}
                  {msg.tableHeaders && msg.tableData && (
                    <StockTable headers={msg.tableHeaders} data={msg.tableData} />
                  )}

                  {/* Confirm prompt buttons */}
                  {msg.action === 'CONFIRM_PROMPT' && (
                    <div className="mt-2 flex gap-2">
                      <button
                        data-testid="confirm-yes-btn"
                        onClick={() => sendMessage(null, 'yes')}
                        className="text-xs px-3 py-1.5 bg-green-900/60 text-green-300 border border-green-700 rounded-lg hover:bg-green-800/60 transition-colors font-semibold"
                      >
                        Yes, create it
                      </button>
                      <button
                        data-testid="confirm-no-btn"
                        onClick={() => sendMessage(null, 'no')}
                        className="text-xs px-3 py-1.5 bg-zinc-800 text-zinc-400 border border-zinc-700 rounded-lg hover:bg-zinc-700 transition-colors"
                      >
                        No, cancel
                      </button>
                    </div>
                  )}
                  {/* Feasibility → one-click "Add all shortages to Order Plan" */}
                  {msg.actionResult?.can_add_to_order_plan && Array.isArray(msg.actionResult.order_plan_payload) && msg.actionResult.order_plan_payload.length > 0 && !msg.orderPlanDone && (
                    <div className="mt-2">
                      <button
                        data-testid={`add-to-order-plan-${i}`}
                        onClick={async () => {
                          try {
                            await Promise.all(
                              msg.actionResult.order_plan_payload.map(p =>
                                axios.post(`${API}/order-plan`, p, { withCredentials: true })
                              )
                            );
                            toast.success(`${msg.actionResult.order_plan_payload.length} item(s) added to Need-to-Order list`);
                            setMessages(prev => prev.map((m, idx) => idx === i ? { ...m, orderPlanDone: true } : m));
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to add to plan');
                          }
                        }}
                        className="text-xs px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black rounded-lg font-semibold transition-colors flex items-center gap-1.5"
                      >
                        <ShoppingCart size={11} /> Add {msg.actionResult.order_plan_payload.length} to Need-to-Order list
                      </button>
                    </div>
                  )}
                  {msg.orderPlanDone && (
                    <div className="mt-2 text-[10px] text-green-400 flex items-center gap-1">✓ Added to Need-to-Order list</div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 flex items-center gap-2">
                  <Loader2 size={13} className="text-red-400 animate-spin" />
                  <span className="text-zinc-500 text-xs font-mono">executing...</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick Commands (shown only at start) */}
          {messages.length <= 1 && (
            <div className="px-3 pb-2 flex gap-1.5 flex-wrap border-t border-zinc-800 pt-2">
              {QUICK_CMDS.map(cmd => (
                <button
                  key={cmd.text}
                  data-testid={`quick-cmd-${cmd.text.replace(/\s+/g, '-')}`}
                  onClick={() => sendMessage(null, cmd.text)}
                  className="text-[11px] px-2.5 py-1 bg-zinc-800/80 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-100 rounded-lg border border-zinc-700 transition-colors flex items-center gap-1.5"
                >
                  <cmd.icon size={10} className="text-zinc-500" />
                  {cmd.text}
                </button>
              ))}
            </div>
          )}

          {/* Autocomplete suggestions */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className="px-3 pb-1 border-t border-zinc-800">
              <div className="flex flex-col">
                {filteredSuggestions.map(s => (
                  <button
                    key={s}
                    onMouseDown={(e) => { e.preventDefault(); setInput(s); setShowSuggestions(false); }}
                    className="text-[11px] text-left text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 px-2 py-1 rounded transition-colors font-mono"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="px-3 py-2.5 border-t border-zinc-800 flex gap-2 flex-shrink-0">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-red-400/60 text-xs font-mono">$</span>
              <input
                ref={inputRef}
                data-testid="ai-chat-input"
                value={input}
                onChange={e => { setInput(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder={recording ? 'Listening... tap mic to stop' : (transcribing ? 'Transcribing...' : 'type command or tap mic')}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl pl-7 pr-3 py-2 text-zinc-50 text-xs font-mono placeholder-zinc-500 focus:outline-none focus:border-red-500 transition-colors"
                disabled={loading || transcribing}
                autoComplete="off"
              />
            </div>
            <button
              type="button"
              data-testid="ai-chat-mic"
              onClick={toggleRecording}
              disabled={loading || transcribing}
              title={recording ? 'Stop recording' : 'Record Hinglish command'}
              className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${
                recording
                  ? 'bg-red-600 hover:bg-red-500 animate-pulse'
                  : transcribing
                    ? 'bg-amber-600'
                    : 'bg-zinc-700 hover:bg-zinc-600'
              }`}
            >
              {transcribing
                ? <Loader2 size={13} className="text-white animate-spin" />
                : recording
                  ? <MicOff size={13} className="text-white" />
                  : <Mic size={13} className="text-zinc-200" />
              }
            </button>
            <button
              data-testid="ai-chat-send"
              type="submit"
              disabled={loading || !input.trim()}
              className="w-8 h-8 rounded-xl bg-red-600 hover:bg-red-500 disabled:bg-zinc-700 flex items-center justify-center transition-colors flex-shrink-0"
            >
              <Send size={13} className={loading || !input.trim() ? 'text-zinc-500' : 'text-white'} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
